// W5-2 / roadmap 7 — THE SHELF LAWS, PULLED OUT SO A GATE CAN RUN THEM.
//
// Every one of these was already pure and already load-bearing, and not one of them could be reached
// by a `.mjs` gate while it sat inside App.tsx: how a category becomes an anchor id, how a title
// becomes a spine that never clips and never breaks mid-word, and how a catalogue becomes rows of
// shelves. The shelf a member browses is built entirely by arithmetic in this file.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law).

export type ShelfBook = {
  slug: string;
  title: string;
  author: string | null;
  mediaType: "book" | "video";
  categories: string[];
  pageCount: number;
};

// A book on Lala's Bookshelf: the family's own books, held as designed page IMAGES rather than
// ingested text, which is why `pageCount` here counts pictures and `hasCover` exists at all.
export type FeaturedBook = {
  slug: string;
  title: string;
  author: string | null;
  pageCount: number;
  hasCover: boolean;
};

// A glyph per shelf category for the category pills.
export const CATEGORY_GLYPHS: Record<string, string> = {
  "African Diaspora": "✶",
  Alchemy: "⚗",
  Egyptian: "☥",
  Astrology: "☉",
  Symbols: "❖",
  "Sacred Sexuality": "♀",
  Tarot: "✦",
};

// V3: every spine is BLACK. A handful of near-black tones give subtle depth variation between
// neighbours, but no spine is ever light or colored — the whole shelf reads black-and-gold only.
// Variation lives in ornament + glyph, never color.
export const SPINE_COLORS = [
  "#0d0b0a",
  "#131010",
  "#0a0908",
  "#151110",
  "#100d0c",
  "#0c0a09",
];

// Deterministic esoteric glyph per title, stamped at the head and foot of the spine.
export const SPINE_GLYPHS = ["☾", "✶", "☉", "♄", "☿", "✦", "☽", "⚹", "✷", "❖", "⸎", "♃"];

export function shelfSlug(category: string) {
  return (
    "shelf-" +
    category
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
  );
}

export function hashString(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}

// Shorten a title to <= max chars by DROPPING whole trailing words — never cuts mid-word
// (e.g. "Fortune-Telling by Cards" -> "Fortune-Telling"). If the first word alone is longer than max
// it is kept whole (a smaller font + taller spine make it fit). The full title stays available on
// hover and inside the reader.
const TRAILING_STOP_WORDS = new Set([
  "the", "a", "an", "of", "to", "and", "or", "by", "in", "on", "for", "with", "from",
  "their", "his", "her", "its", "this", "that", "these", "those", "our", "your",
]);

export function shortenToWords(title: string, max: number) {
  const trimmed = title.trim();
  // A complete title is shown in full (the spine is sized to fit it).
  if (trimmed.length <= max) return trimmed.replace(/[\s,;:]+$/, "");
  const words = trimmed.split(/\s+/);
  let kept = [words[0]];
  for (let index = 1; index < words.length; index += 1) {
    if (`${kept.join(" ")} ${words[index]}`.length > max) break;
    kept.push(words[index]);
  }
  // Drop trailing connectors so a shortened label never ends on "to"/"the"/etc.
  while (
    kept.length > 1 &&
    TRAILING_STOP_WORDS.has(kept[kept.length - 1].toLowerCase().replace(/[,;:]$/, ""))
  ) {
    kept.pop();
  }
  return kept.join(" ").replace(/[\s,;:]+$/, "");
}

// V3 spine: the title reads as HORIZONTAL words stacked down the spine (like the mockup), never
// rotated vertical text. The book is sized WIDE enough to hold its longest word horizontally, and
// TALL enough to hold one line per word plus the two ornamental gold bands — so a title is never
// clipped and never breaks mid-word.
export function fitSpine(title: string) {
  const hash = hashString(title);
  const display = shortenToWords(title, 34);
  const words = display.split(/\s+/);
  const longest = words.reduce((max, word) => Math.max(max, word.length), 0);

  // Font steps DOWN as the longest word grows so it always fits the spine width.
  const fontRem =
    longest <= 5 ? 0.82 : longest <= 8 ? 0.72 : longest <= 11 ? 0.62 : longest <= 14 ? 0.54 : 0.48;

  // Width holds the longest word horizontally, plus the gold frame + padding.
  const charPx = fontRem * 16 * 0.6;
  const width = Math.round(Math.min(158, Math.max(94, longest * charPx + 40)));

  // Height holds one line per word, plus the head + foot bands and padding, with a little
  // deterministic jitter so neighbouring spines vary in height.
  const lineH = fontRem * 16 * 1.34;
  const reserved = 84;
  const jitter = (hash % 3) * 7;
  const height = Math.round(
    Math.min(324, Math.max(186, words.length * lineH + reserved + jitter)),
  );

  return {
    display,
    words,
    height,
    width,
    color: SPINE_COLORS[(hash >>> 6) % SPINE_COLORS.length],
    glyph: SPINE_GLYPHS[(hash >>> 9) % SPINE_GLYPHS.length],
    ornament: (hash >>> 12) % 3,
    fontRem,
  };
}

// Build shelves ordered by category size (largest first). Categories with fewer than
// SHELF_SOLO_MIN books share a row (each keeps its own brass plate), leaving room to grow; larger
// categories get their own shelf. Fully data-driven.
export const SHELF_SOLO_MIN = 5; // 5+ books => own shelf; <=4 shares a row
export type ShelfCluster = { category: string; books: ShelfBook[] };
export type ShelfRow = { shared: boolean; clusters: ShelfCluster[]; count: number };

export function buildShelves(shelfBooks: ShelfBook[]) {
  const byCategory = new Map<string, ShelfBook[]>();
  for (const book of shelfBooks) {
    for (const category of book.categories) {
      const list = byCategory.get(category) ?? [];
      list.push(book);
      byCategory.set(category, list);
    }
  }
  const categories: ShelfCluster[] = [...byCategory.entries()]
    .map(([category, list]) => ({
      category,
      books: [...list].sort((a, b) => a.title.localeCompare(b.title)),
    }))
    .sort((a, b) => b.books.length - a.books.length || a.category.localeCompare(b.category));

  const rows: ShelfRow[] = [];
  let sharedRow: ShelfRow | null = null;
  for (const cluster of categories) {
    if (cluster.books.length >= SHELF_SOLO_MIN) {
      rows.push({ shared: false, clusters: [cluster], count: cluster.books.length });
    } else {
      if (!sharedRow || sharedRow.count + cluster.books.length > 12) {
        sharedRow = { shared: true, clusters: [], count: 0 };
        rows.push(sharedRow);
      }
      sharedRow.clusters.push(cluster);
      sharedRow.count += cluster.books.length;
    }
  }
  return { categories, rows };
}

// R44 — **WHICH DATABASE ROW A TITLE ON A SHELF OPENS.** Moved out of App.tsx, where it sat 200 lines
// from the reader that calls it and where no gate could load it.
//
// THE CROSSING THIS LAW LIVES ON, NAMED SO IT IS NOT RE-LEARNED: the ingest writes a book's slug
// (`server/ingest/overnight.mjs` — lowercase, non-alphanumerics to a dash, trimmed, cut at 80) and the
// client RE-DERIVES that same slug from the catalog title to ask for the pages. The two rules are
// typed out separately, and when they disagree the reader does not error — it opens a book with no
// text in it. `READER_SLUG_OVERRIDES` is the hand-written patch for the one title where they already
// do disagree, which is the evidence that they can.
const READER_SLUG_OVERRIDES: Record<string, string> = {
  "The Hermetic Museum": "the-hermetic-museum-volume-i",
};
export function bookSlug(title: string) {
  return (
    READER_SLUG_OVERRIDES[title] ||
    title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80)
  );
}

// R59 — ONE NORMALISER FOR A SHELF ROW, because it was typed out twice byte for byte: My Room's
// saved shelf and the Library's own catalogue both took the server's JSON and re-typed the same
// seven coercions over it, 9,500 lines apart. R47's currency, where the COUNT is the finding.
//
// Every field is COERCED rather than trusted, and that is the point of having it out here where a
// gate can run it: a row missing `categories` must become `[]` and not `undefined`, because
// `buildShelves` above groups on it, and a `pageCount` that arrives as a string must become a number
// because the spine arithmetic divides by it. A row with no slug is not a book and answers null —
// the alternative is a shelf drawing a spine that opens nothing.
export function shelfBookFrom(row: Record<string, unknown> | null | undefined): ShelfBook | null {
  if (!row || typeof row !== "object" || !row.slug) return null;
  return {
    slug: String(row.slug),
    title: String(row.title ?? ""),
    author: (row.author as string) ?? null,
    mediaType: row.mediaType === "video" ? "video" : "book",
    categories: Array.isArray(row.categories) ? (row.categories as string[]) : [],
    pageCount: Number(row.pageCount) || 0,
  };
}

// The list form, with the unbook rows dropped rather than turned into holes in the shelf.
export function shelfBooksFrom(rows: unknown): ShelfBook[] {
  return (Array.isArray(rows) ? rows : [])
    .map((r) => shelfBookFrom(r as Record<string, unknown>))
    .filter((b): b is ShelfBook => b !== null);
}
